import {IntlShape, defineMessages} from 'react-intl'

/**
 * Catálogo de Facturación de Twilio (`/twilio-billing`): la pestaña de coste y
 * uso de la cuenta, y la pestaña de inventario de números.
 *
 * No se traduce:
 * - **Twilio, VICIdial, PolicyDial, SMS, WhatsApp, MMS, A2P 10DLC, SIP**:
 *   nombres de proveedor, producto, canal y estándar del sector.
 * - **`c.description` y `c.usage_unit`**: los sirve la API de Twilio ya
 *   redactados (nombre de la categoría y su unidad). Lo que sí traducimos es
 *   `CATEGORY_HELP`, que es glosa nuestra.
 * - **`n.type`** (`local` / `tollfree`) y **`n.classification`** en crudo: son
 *   valores de la API. La etiqueta de la clasificación sí se traduce, vía
 *   `classLabel()`.
 * - Los nombres de estado de `US_STATES` (topónimos de EE. UU.) y los E.164.
 */
export const EM_DASH = '—'

export const BILLING_MESSAGES = defineMessages({
  // ── Cabecera ──
  title: {id: 'telephony.billing.title', defaultMessage: 'Billing'},
  subtitle: {
    id: 'telephony.billing.subtitle',
    defaultMessage: 'Twilio account spend for the organization.',
  },
  errorTitle: {id: 'telephony.billing.error.title', defaultMessage: 'Twilio billing unavailable'},

  // ── Tarjetas de resumen ──
  kpiThisMonth: {id: 'telephony.billing.kpi.thisMonth', defaultMessage: 'This month'},
  kpiLastMonth: {id: 'telephony.billing.kpi.lastMonth', defaultMessage: 'Last month'},
  kpiBalance: {id: 'telephony.billing.kpi.balance', defaultMessage: 'Balance'},
  kpiLowBalance: {id: 'telephony.billing.kpi.lowBalance', defaultMessage: 'Low balance'},
  kpiVsLastMonth: {
    id: 'telephony.billing.kpi.vsLastMonth',
    defaultMessage: '{arrow} {percent}% vs last month',
  },

  // ── Gasto mensual ──
  trendTitle: {id: 'telephony.billing.trend.title', defaultMessage: 'Monthly spend'},
  trendHint: {
    id: 'telephony.billing.trend.hint',
    defaultMessage: 'Click a month to see its breakdown →',
  },
  trendBarTitle: {
    id: 'telephony.billing.trend.barTitle',
    defaultMessage: 'Show {month} breakdown',
  },

  // ── Desglose por categoría ──
  breakdownTitle: {id: 'telephony.billing.breakdown.title', defaultMessage: '{month} by category'},
  breakdownBack: {
    id: 'telephony.billing.breakdown.back',
    defaultMessage: '← Back to this month',
  },
  breakdownDownloadPdf: {
    id: 'telephony.billing.breakdown.downloadPdf',
    defaultMessage: 'Download PDF',
  },
  breakdownNote: {
    id: 'telephony.billing.breakdown.note',
    defaultMessage:
      "Each category is shown at Twilio's standard (list) price. Your account discount is applied to the subtotal, so the <b>Total</b> is what Twilio actually bills.",
  },
  colCategory: {id: 'telephony.billing.column.category', defaultMessage: 'Category'},
  colWhatItIs: {id: 'telephony.billing.column.whatItIs', defaultMessage: 'What it is'},
  colUsage: {id: 'telephony.billing.column.usage', defaultMessage: 'Usage'},
  colCost: {id: 'telephony.billing.column.cost', defaultMessage: 'Cost'},
  rowSubtotal: {id: 'telephony.billing.row.subtotal', defaultMessage: 'Subtotal'},
  rowSubtotalHelp: {
    id: 'telephony.billing.row.subtotalHelp',
    defaultMessage: 'Usage at Twilio list price',
  },
  rowAdjustment: {
    id: 'telephony.billing.row.adjustment',
    defaultMessage: 'Discount & adjustments',
  },
  rowAdjustmentHelp: {
    id: 'telephony.billing.row.adjustmentHelp',
    defaultMessage: 'Your Twilio account discount (not itemized by Twilio)',
  },
  rowTotalBilled: {id: 'telephony.billing.row.totalBilled', defaultMessage: 'Total billed'},
  rowTotalBilledHelp: {
    id: 'telephony.billing.row.totalBilledHelp',
    defaultMessage: 'What Twilio actually charges',
  },

  // ── PDF exportable ──
  pdfTitle: {id: 'telephony.billing.pdf.title', defaultMessage: 'Twilio Usage & Billing'},
  pdfGenerated: {id: 'telephony.billing.pdf.generated', defaultMessage: 'Generated {date}'},
  pdfAccountBalance: {
    id: 'telephony.billing.pdf.accountBalance',
    defaultMessage: 'Account balance: {amount}',
  },
  pdfNote: {
    id: 'telephony.billing.pdf.note',
    defaultMessage:
      "Each category is shown at Twilio's standard (list) price. Your account discount is applied to the subtotal, so the Total billed is what Twilio actually charges for the period.",
  },

  // ── Inventario de números ──
  invErrorTitle: {
    id: 'telephony.billing.inventory.error.title',
    defaultMessage: 'Numbers inventory unavailable',
  },
  invTitle: {id: 'telephony.billing.inventory.title', defaultMessage: 'Numbers inventory'},
  invTitleCount: {
    id: 'telephony.billing.inventory.titleCount',
    defaultMessage: 'Numbers inventory ({count})',
  },
  invStalePool: {
    id: 'telephony.billing.inventory.stalePool',
    defaultMessage: 'Stale pool: {count}',
  },
  invFilterBy: {id: 'telephony.billing.inventory.filterBy', defaultMessage: 'Filter by {label}'},
  invClearFilter: {id: 'telephony.billing.inventory.clearFilter', defaultMessage: 'Clear filter'},
  invSearchPlaceholder: {
    id: 'telephony.billing.inventory.searchPlaceholder',
    defaultMessage: 'Search number…',
  },
  invReviewOnly: {
    id: 'telephony.billing.inventory.reviewOnly',
    defaultMessage: 'Review candidates only',
  },
  invExportCsv: {id: 'telephony.billing.inventory.exportCsv', defaultMessage: 'Export CSV'},
  invAddToPool: {id: 'telephony.billing.inventory.addToPool', defaultMessage: 'Add number to pool'},
  invColPurchased: {
    id: 'telephony.billing.inventory.column.purchased',
    defaultMessage: 'Purchased',
  },
  invColRental: {
    id: 'telephony.billing.inventory.column.rentalPerMonth',
    defaultMessage: 'Rental/mo',
  },
  invColUsedFor: {id: 'telephony.billing.inventory.column.usedFor', defaultMessage: 'Used for'},
  invColOwnerLastUsed: {
    id: 'telephony.billing.inventory.column.ownerLastUsed',
    defaultMessage: 'Owner / Last used',
  },
  invStale: {id: 'telephony.billing.inventory.stale', defaultMessage: 'stale'},
  invUsedOn: {id: 'telephony.billing.inventory.usedOn', defaultMessage: 'used {date}'},
  invNoMatches: {id: 'telephony.billing.inventory.noMatches', defaultMessage: 'No numbers match'},

  // ── Inventario: liberar ──
  invConfirmRelease: {
    id: 'telephony.billing.inventory.confirm.release',
    defaultMessage:
      'Release {number}? This permanently gives up the number on Twilio and stops its ~{amount}/mo charge.',
  },
  invConfirmWarnMessaging: {
    id: 'telephony.billing.inventory.confirm.warnMessaging',
    defaultMessage: '⚠️ This number is used for SMS/WhatsApp. Releasing it will break that.',
  },
  invConfirmWarnPool: {
    id: 'telephony.billing.inventory.confirm.warnPool',
    defaultMessage: 'It will be removed from the local-presence dialing pool.',
  },
  invToastReleased: {
    id: 'telephony.billing.inventory.toast.released',
    defaultMessage: '{number} released.',
  },
  invToastReleaseFailed: {
    id: 'telephony.billing.inventory.toast.releaseFailed',
    defaultMessage: 'Could not release the number',
  },
  invToastNothingToExport: {
    id: 'telephony.billing.inventory.toast.nothingToExport',
    defaultMessage: 'Nothing to export yet.',
  },
  invToastPickState: {
    id: 'telephony.billing.inventory.toast.pickState',
    defaultMessage: 'Pick a state first.',
  },
  invToastAddedToPool: {
    id: 'telephony.billing.inventory.toast.addedToPool',
    defaultMessage: 'Numbers added to the pool.',
  },
  invToastAddFailed: {
    id: 'telephony.billing.inventory.toast.addFailed',
    defaultMessage: 'Could not add numbers to the pool',
  },

  // ── Inventario: modal de compra ──
  buyTitle: {
    id: 'telephony.billing.inventory.buy.title',
    defaultMessage: 'Add numbers to the pool',
  },
  buyTitleDone: {
    id: 'telephony.billing.inventory.buy.titleDone',
    defaultMessage: 'Numbers added to the pool',
  },
  buyDescription: {
    id: 'telephony.billing.inventory.buy.description',
    defaultMessage:
      "Buy local-presence numbers and add them to VICIdial's outbound Caller-ID pool (rotated by the lead's state). No burned number required.",
  },
  buyAreaCode: {
    id: 'telephony.billing.inventory.buy.areaCode',
    defaultMessage: 'Area code (optional)',
  },
  buyAreaCodePlaceholder: {
    id: 'telephony.billing.inventory.buy.areaCodePlaceholder',
    defaultMessage: 'e.g. 305',
  },
  buyQuantity: {id: 'telephony.billing.inventory.buy.quantity', defaultMessage: 'Quantity'},
  buyEstCost: {
    id: 'telephony.billing.inventory.buy.estCost',
    defaultMessage: 'Est. cost ≈ {amount}/mo (max 10 per purchase).',
  },
  buyResult: {
    id: 'telephony.billing.inventory.buy.result',
    defaultMessage:
      '{count, plural, one {Bought <b>#</b> number for <b>{state}</b> — added to the pool.} other {Bought <b>#</b> numbers for <b>{state}</b> — added to the pool.}}',
  },
  buyColCost: {id: 'telephony.billing.inventory.buy.colCost', defaultMessage: 'Cost/mo'},
  buyConfirm: {
    id: 'telephony.billing.inventory.buy.confirm',
    defaultMessage: 'Buy & add to pool',
  },
  buyMore: {id: 'telephony.billing.inventory.buy.more', defaultMessage: 'Buy more'},

  // ── Cabeceras del CSV exportado ──
  csvFriendlyName: {
    id: 'telephony.billing.inventory.csv.friendlyName',
    defaultMessage: 'Friendly Name',
  },
  csvMonthlyRental: {
    id: 'telephony.billing.inventory.csv.monthlyRental',
    defaultMessage: 'Monthly Rental (est.)',
  },
  csvOwner: {id: 'telephony.billing.inventory.csv.owner', defaultMessage: 'Owner'},
  csvLastUsed: {id: 'telephony.billing.inventory.csv.lastUsed', defaultMessage: 'Last Used'},
  csvStale: {id: 'telephony.billing.inventory.csv.stale', defaultMessage: 'Stale'},

  // ── Reusadas de common.* ──
  monthYear: {id: 'common.date.monthYear', defaultMessage: '{month} {year}'},
  colNumber: {id: 'common.field.number', defaultMessage: 'Number'},
  csvPhoneNumber: {id: 'common.field.phoneNumber', defaultMessage: 'Phone Number'},
  colType: {id: 'common.field.type', defaultMessage: 'Type'},
  colState: {id: 'common.field.state', defaultMessage: 'State'},
  colActions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  colGroup: {id: 'common.field.group', defaultMessage: 'Group'},
  colOrganization: {id: 'common.field.organization', defaultMessage: 'Organization'},
  total: {id: 'common.field.total', defaultMessage: 'Total'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  done: {id: 'common.action.done', defaultMessage: 'Done'},
  previous: {id: 'common.action.previous', defaultMessage: 'Previous'},
  next: {id: 'common.action.next', defaultMessage: 'Next'},
  yes: {id: 'common.boolean.yes', defaultMessage: 'Yes'},
  no: {id: 'common.boolean.no', defaultMessage: 'No'},
  showingRange: {
    id: 'common.pagination.showingRange',
    defaultMessage: 'Showing {start}–{end} of {total}',
  },
  pageIndicator: {id: 'common.pagination.pageIndicator', defaultMessage: '{page} / {total}'},
})

/**
 * Glosa en lenguaje llano de cada categoría de consumo de Twilio, indexada por
 * el código estable de la categoría (el nombre "amigable" que devuelve la API
 * suele ser opaco — p. ej. "Channels"). Texto nuestro, así que se traduce.
 */
const CATEGORY_HELP = defineMessages({
  'phonenumbers-local': {
    id: 'telephony.billing.categoryHelp.phonenumbersLocal',
    defaultMessage: 'Monthly rental of your local phone numbers',
  },
  'phonenumbers-tollfree': {
    id: 'telephony.billing.categoryHelp.phonenumbersTollfree',
    defaultMessage: 'Monthly rental of your toll-free numbers',
  },
  channels: {
    id: 'telephony.billing.categoryHelp.channels',
    defaultMessage: 'Twilio Conversations / Programmable Chat messages (omnichannel messaging)',
  },
  'channels-messaging': {
    id: 'telephony.billing.categoryHelp.channelsMessaging',
    defaultMessage: 'Conversations messaging platform fee',
  },
  'channels-messaging-inbound': {
    id: 'telephony.billing.categoryHelp.channelsMessagingInbound',
    defaultMessage: 'Inbound messages through Conversations channels',
  },
  'channels-messaging-outbound': {
    id: 'telephony.billing.categoryHelp.channelsMessagingOutbound',
    defaultMessage: 'Outbound messages through Conversations channels',
  },
  'sms-outbound-longcode': {
    id: 'telephony.billing.categoryHelp.smsOutboundLongcode',
    defaultMessage: 'Outbound SMS sent from your 10-digit numbers',
  },
  'sms-inbound-longcode': {
    id: 'telephony.billing.categoryHelp.smsInboundLongcode',
    defaultMessage: 'Inbound SMS received on your numbers',
  },
  'sms-messages-carrierfees': {
    id: 'telephony.billing.categoryHelp.smsCarrierFees',
    defaultMessage: 'Carrier pass-through fees charged on each SMS',
  },
  'bundle-sms-bucket': {
    id: 'telephony.billing.categoryHelp.bundleSmsBucket',
    defaultMessage: 'Prepaid US SMS bundle (bucket pricing)',
  },
  'trunking-termination': {
    id: 'telephony.billing.categoryHelp.trunkingTermination',
    defaultMessage: 'Outbound call minutes over the SIP trunk (VICIdial dialing)',
  },
  'trunking-origination-local': {
    id: 'telephony.billing.categoryHelp.trunkingOriginationLocal',
    defaultMessage: 'Inbound call minutes over the SIP trunk',
  },
  'a2p-registration-fees': {
    id: 'telephony.billing.categoryHelp.a2pRegistrationFees',
    defaultMessage: 'A2P 10DLC messaging registration',
  },
  'a2p-10dlc-registrationfees-campaigncharges': {
    id: 'telephony.billing.categoryHelp.a2pCampaignCharges',
    defaultMessage: 'A2P 10DLC campaign registration fee',
  },
  'a2p-10dlc-registrationfees-monthly': {
    id: 'telephony.billing.categoryHelp.a2pMonthly',
    defaultMessage: 'A2P 10DLC monthly registration fee',
  },
  'mms-inbound': {
    id: 'telephony.billing.categoryHelp.mmsInbound',
    defaultMessage: 'Inbound MMS (picture messages)',
  },
  'mms-messages-carrierfees': {
    id: 'telephony.billing.categoryHelp.mmsCarrierFees',
    defaultMessage: 'Carrier pass-through fees charged on each MMS',
  },
  transcriptions: {
    id: 'telephony.billing.categoryHelp.transcriptions',
    defaultMessage: 'Voice call transcriptions',
  },
  'calls-inbound-local': {
    id: 'telephony.billing.categoryHelp.callsInboundLocal',
    defaultMessage: 'Inbound voice call minutes',
  },
  recordingstorage: {
    id: 'telephony.billing.categoryHelp.recordingStorage',
    defaultMessage: 'Monthly storage of call recordings',
  },
  'calls-recordings': {
    id: 'telephony.billing.categoryHelp.callsRecordings',
    defaultMessage: 'Call recording minutes',
  },
  'enhanced-line-type-lookups': {
    id: 'telephony.billing.categoryHelp.lineTypeLookups',
    defaultMessage: 'Line Type Intelligence phone lookups',
  },
  'failed-message-processing-fee': {
    id: 'telephony.billing.categoryHelp.failedMessageFee',
    defaultMessage: 'Fee for messages that failed to send',
  },
  __other__: {
    id: 'telephony.billing.categoryHelp.other',
    defaultMessage: 'Smaller categories grouped together',
  },
})

// `trunking-origination` y `trunking-origination-local` comparten glosa.
const CATEGORY_HELP_ALIAS: Record<string, keyof typeof CATEGORY_HELP> = {
  'trunking-origination': 'trunking-origination-local',
}

/** Categoría desconocida (Twilio añade unas cuantas al año) → sin glosa. */
export const categoryHelp = (intl: IntlShape, category: string): string => {
  const key = CATEGORY_HELP_ALIAS[category] ?? (category as keyof typeof CATEGORY_HELP)
  const descriptor = CATEGORY_HELP[key]
  return descriptor ? intl.formatMessage(descriptor) : ''
}

export type Classification = 'pool' | 'policydial' | 'messaging' | 'unclassified'

/** Etiqueta de la clasificación del número. "SMS / WhatsApp" son canales: no se traducen. */
const CLASS_LABEL = defineMessages({
  pool: {id: 'telephony.billing.inventory.class.pool', defaultMessage: 'Local-presence pool'},
  policydial: {
    id: 'telephony.billing.inventory.class.policydial',
    defaultMessage: 'PolicyDial (agent)',
  },
  messaging: {
    id: 'telephony.billing.inventory.class.messaging',
    defaultMessage: 'SMS / WhatsApp',
  },
  unclassified: {
    id: 'telephony.billing.inventory.class.unclassified',
    defaultMessage: 'Unclassified',
  },
})

export const classLabel = (intl: IntlShape, c: Classification): string =>
  CLASS_LABEL[c] ? intl.formatMessage(CLASS_LABEL[c]) : c
